import React from 'react';
import type { WorkflowStatus } from '../../types/emma.types';

interface WorkflowStatusIndicatorProps {
  status: WorkflowStatus;
  className?: string;
}

/**
 * WorkflowStatusIndicator - Shows real-time workflow status
 * Part of MVP-UX-001: Emma Continuity System
 */
export function WorkflowStatusIndicator({ status, className = '' }: WorkflowStatusIndicatorProps) {
  const getStatusIcon = () => {
    switch (status.type) {
      case 'agent_executing':
        return (
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
        );
      case 'waiting_user_input':
        return (
          <div className="h-4 w-4 rounded-full bg-yellow-500 animate-pulse" />
        );
      case 'processing':
        return (
          <div className="h-4 w-4 rounded-full bg-blue-500">
            <div className="animate-ping absolute inline-flex h-4 w-4 rounded-full bg-blue-400 opacity-75"></div>
          </div>
        );
      case 'complete':
        return (
          <div className="h-4 w-4 rounded-full bg-green-500 flex items-center justify-center">
            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="h-4 w-4 rounded-full bg-red-500 flex items-center justify-center">
            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (status.type) {
      case 'agent_executing':
      case 'processing':
        return 'text-blue-600 dark:text-blue-400';
      case 'waiting_user_input':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'complete':
        return 'text-green-600 dark:text-green-400';
      case 'error':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {getStatusIcon()}
      <div className="flex flex-col">
        <span className={`text-xs font-medium ${getStatusColor()}`}>
          {status.description}
        </span>
        {status.progress && (
          <div className="w-16 h-1 bg-gray-200 dark:bg-gray-700 rounded-full mt-1">
            <div 
              className="h-1 bg-blue-500 rounded-full transition-all duration-300"
              style={{ width: `${status.progress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
}